import { apiv1 } from '@/adapters';
import type {
  InvitationModel,
  InvitationResult as InvitationResultModel,
  MemberModel,
} from '@/adapters/apiv1.g';
import {
  extractAccepted,
  extractBoolean,
  extractInvitation,
  extractInvitationResult,
  extractMember,
  extractPageModels,
  unwrapData,
} from '@/adapters/shared';
import type { Invitation, InvitationResult, Member } from '@/types/core/member';
import type { Page, PageRequest } from '@/types/shared/page';
import { toApiQuery } from '@/types/shared/page';
import { type UUID } from '@/utils/uuid';

export const memberService = {
  /** List all members in a tenant (paged) */
  listMembers: async (
    tenantId: UUID,
    query?: PageRequest
  ): Promise<Page<Member>> => {
    const apiQuery = toApiQuery(query);
    const res = await apiv1.listMembers(tenantId, apiQuery);
    const models = extractPageModels<MemberModel>(res);
    const next = res?.data?.next ?? null;
    const prev = res?.data?.prev ?? null;
    return {
      items: models.map(extractMember),
      next,
      prev,
      limit: query?.limit,
    };
  },

  /** Invite a member to the tenant */
  inviteMember: async (
    tenantId: UUID,
    invitationId: UUID,
    body: { email: string }
  ): Promise<InvitationResult | undefined> => {
    const payload: { email: string } = {
      email: body.email,
    };
    const res = await apiv1.inviteMember(tenantId, invitationId, payload);
    const model = unwrapData<InvitationResultModel>(res);
    if (!model) return undefined;
    return extractInvitationResult(model);
  },

  /** List all member invitations for the tenant */
  listInvitations: async (
    tenantId: UUID,
    query?: PageRequest
  ): Promise<Page<Invitation>> => {
    const apiQuery = toApiQuery(query);
    const res = await apiv1.listInvitations(tenantId, apiQuery);
    const models = extractPageModels<InvitationModel>(res);
    const next = res?.data?.next ?? null;
    const prev = res?.data?.prev ?? null;
    return {
      items: models.map(extractInvitation),
      next,
      prev,
      limit: query?.limit,
    };
  },

  /** Remove a member from the tenant (adapter: removeMember) */
  removeMember: async (tenantId: UUID, userId: UUID): Promise<boolean> => {
    const res = await apiv1.removeMember(tenantId, userId);
    return extractBoolean(res);
  },

  /** Find a member in the tenant by user ID */
  findMember: async (
    tenantId: UUID,
    userId: UUID
  ): Promise<Member | undefined> => {
    const res = await apiv1.findMember(tenantId, userId);
    const model = unwrapData<MemberModel>(res);
    if (!model) return undefined;
    return extractMember(model);
  },

  /** Add or update a member in the tenant (adapter: addMember) */
  addMember: async (tenantId: UUID, userId: UUID): Promise<boolean> => {
    const res = await apiv1.addMember(tenantId, { user_id: userId });
    return extractAccepted(res);
  },

  /** Find an invitation by id */
  findInvitation: async (
    tenantId: UUID,
    invitationId: UUID
  ): Promise<Invitation | undefined> => {
    const res = await apiv1.findInvitation(tenantId, invitationId);
    const model = unwrapData<InvitationModel>(res);
    if (!model) return undefined;
    return extractInvitation(model);
  },

  /** Revoke/cancel an invitation */
  revokeInvitation: async (
    tenantId: UUID,
    invitationId: UUID
  ): Promise<boolean> => {
    const res = await apiv1.removeInvitation(tenantId, invitationId);
    return extractBoolean(res);
  },
};
